import { useCallback, useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "context/AuthContext";
import TextInput from "components/ui/TextInput";
import CommonModal from "components/ui/CommonModal";
import { isTaskResumeRedirect, safeRedirect } from "util/pendingPosting";
import WebRoleOnboarding from "components/muyeon/WebRoleOnboarding";
import { callCompleteSocial, callGetNiceIdentityResult, callNiceIdentityConfig } from "api/MuyeonApi";
import {
  startIdentityVerification,
  popIdentityForm,
  NICE_TOKEN_PARAM,
  NICE_ERROR_PARAM,
  NICE_ERROR_MESSAGE,
} from "util/niceIdentity";
import { openNativeOrWeb } from "common/nativeRoute";
import { openRoleOnboarding } from "common/nativeBridge";
import { pixelTrack } from "util/metaPixel";

// 소셜 자동가입 시 백엔드가 넣는 기본 이름 → 입력칸은 비워서 실제 이름을 받도록.
const DEFAULT_NAMES = ["애플회원", "카카오회원"];

/**
 * 소셜 신규회원 추가정보 입력 (이름·회원유형 필수).
 *  - 카카오/애플 로그인에서 isNew=true 일 때만 진입.
 *  - 일반 회원가입과 동일 마크업(#join). 이메일/비번은 이미 소셜로 처리돼 제외.
 */
const SocialSignup = () => {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/home";

  const initName = DEFAULT_NAMES.includes(user?.name) ? "" : user?.name || "";

  const [f, setF] = useState({ name: initName });
  const [msg, setMsg] = useState("");
  const [done, setDone] = useState(false);
  const [webRole, setWebRole] = useState(false); // 웹 회원유형 선택 온보딩(네이티브 브릿지 없을 때)
  const [idAvailable, setIdAvailable] = useState(false);
  const [idRequired, setIdRequired] = useState(false);
  const [identity, setIdentity] = useState(null);
  const [idBusy, setIdBusy] = useState(false);

  const on = (k) => (e) => setF({ ...f, [k]: e?.target ? e.target.value : e.value });

  useEffect(() => {
    callNiceIdentityConfig().then(({ data }) => {
      setIdAvailable(!!data?.available);
      setIdRequired(!!data?.required);
    }).catch(() => {});
  }, []);

  useEffect(() => {
    const token = params.get(NICE_TOKEN_PARAM);
    const err = params.get(NICE_ERROR_PARAM);
    // 결과 없이 돌아와도(인증 화면에서 뒤로가기·앱 '돌아가기') 떠나기 전 입력을 되살린다 — Register 와 같다.
    const saved = popIdentityForm();
    if (saved?.form) setF((prev) => ({ ...prev, ...saved.form }));
    if (!token && !err) return;
    window.history.replaceState(null, "", `/social-signup${redirect !== "/home" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`);
    if (err) { setMsg(NICE_ERROR_MESSAGE[err] || NICE_ERROR_MESSAGE.FAILED); return; }
    callGetNiceIdentityResult(token).then(({ data }) => {
      setIdentity({ token, name: data?.name || "", phone: data?.phone || "" });
      setF((prev) => ({ ...prev, name: data?.name || prev.name }));
    }).catch((e) => setMsg(e?.response?.data?.message || "본인인증 결과를 확인하지 못했어요."));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const verifyIdentity = useCallback(async () => {
    if (idBusy) return;
    setIdBusy(true);
    try {
      await startIdentityVerification({
        returnTo: `/social-signup${redirect !== "/home" ? `?redirect=${encodeURIComponent(redirect)}` : ""}`,
        form: { form: f },
      });
    } catch (e) {
      setMsg(e?.response?.data?.message || e?.message || "본인인증을 시작하지 못했어요.");
      setIdBusy(false);
    }
  }, [idBusy, redirect, f]);

  const submit = async () => {
    // 다중역할 개편: 역할 선택 없음(일반회원). 역할은 MY에서 추가.
    if (!f.name) {
      setMsg("이름을 입력해 주세요.");
      return;
    }
    if (idRequired && !identity) {
      setMsg("휴대폰 본인인증을 완료해 주세요.");
      return;
    }
    try {
      setMsg("");
      // 닉네임 미전달 — 표시명이 실명으로 통일돼 노출되지 않는다(서버가 이름으로 채운다).
      const r = await callCompleteSocial({
        name: f.name,
        identityToken: identity?.token,
      });
      updateUser(r.data); // 마이페이지 활동명/이름/유형 즉시 반영
      pixelTrack("CompleteRegistration", { content_name: "social" }); // 광고 전환(Meta) — 픽셀 ID 없으면 무시
      setDone(true);
    } catch (e) {
      const m = e?.response?.data?.message;
      setMsg(Array.isArray(m) ? m.join("\n") : m || "저장에 실패했어요.");
    }
  };

  return (
    <div className="container">
      <div className="top-bar">
        {/* 가입을 끝내지 않으면 다른 화면이 이 화면으로 되돌아온다 — 나가려면 로그아웃(다음에 소셜 로그인하면 여기부터 다시). */}
        <button type="button" className="ico-btn back" onClick={() => { try { logout(); } catch (e) { /* noop */ } navigate("/home", { replace: true }); }}>
          <i className="arrow" />
          <span className="visually-hidden">가입 그만두기</span>
        </button>
        <h2 className="page-name">추가 정보 입력</h2>
      </div>

      <div id="join" className="page">
        <div className="content-wrap">
          <p className="page-tit">서비스 이용에 필요한 정보를 입력해 주세요</p>

          <div className="tab-content-group">
            <div className="tab-content is-show">
              <ul className="main form-list">
                {/* 본인확인을 마쳤으면 이름은 인증 결과가 정본이다(서버도 인증값으로 덮어쓴다).
                    Register.js 와 같은 규칙 — 여기만 열어 두면 인증받은 이름을 지우고
                    다른 이름으로 가입할 수 있다. */}
                <li className="list-item">
                  <TextInput label="이름" name="name" required isValid
                    value={f.name} onChange={on("name")} placeholder="이름" maxLength={30}
                    isDisabled={!!identity} />
                </li>

                {(idAvailable || idRequired) && <li className="list-item">
                  {identity ? <p className="t-14-single" style={{ color: "#F58232", fontWeight: 700 }}>✓ 본인인증 완료</p>
                    : <button type="button" className="btn-md outlined" disabled={idBusy || !idAvailable} onClick={verifyIdentity}>{idBusy ? "이동 중…" : "휴대폰 본인인증"}</button>}
                </li>}
              </ul>

              {msg && (
                <p className="t-14-single" style={{ color: "#e2362d", padding: "0 2rem" }}>{msg}</p>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="tab-bar-btn-group fixed">
        <div className="btn-group">
          <button type="button" className="btn-md filled primary" onClick={submit}>시작하기</button>
        </div>
      </div>

      <CommonModal
        isShow={done}
        closeModal={() => {
          setDone(false);
          // 광고 유입 공고 작성 중 가입 → 활동 선택 없이 바로 돌아가 공고부터 등록(util/pendingPosting).
          if (isTaskResumeRedirect(redirect)) { // 광고 공고·레슨 요청 중 가입 → 유형 선택 없이 하던 일로
            navigate(safeRedirect(redirect), { replace: true });
            return;
          }
          // 웹: 대체 라우트가 없으므로(매핑 테이블 null) 웹 전용 회원유형 선택 모달로 폴백.
          openNativeOrWeb(openRoleOnboarding, {}, navigate, { onUnavailable: () => setWebRole(true) });
        }}
        content={{ title: "환영해요! 가입이 완료됐어요.", body: null }}
      />

      <WebRoleOnboarding isShow={webRole} redirect={redirect} />
    </div>
  );
};

export default SocialSignup;
